import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ref } from 'lit/directives/ref.js';
import BoilerplateForm from './boilerplate_form';
import { map } from 'lit/directives/map.js';
import { html, nothing } from 'lit';
import formatMoney from '../utilities/formatMoney';
export class GiveawayForm extends BoilerplateForm {
  static get properties() {
    return {
      value: { type: String },
      token: { type: String },
      countries: { type: Array, attribute: true, reflect: false },
      customerDetails: {
        type: Object,
        attribute: 'customer-details',
        reflect: false,
      },
      giveawayHandle: {
        type: String,
        attribute: 'giveaway-handle',
        reflect: false,
      },
      currentOrder: {
        type: Object,
        state: true,
      },
      stepsImageMobile: {
        type: String,
        attribute: 'steps-image-mobile',
      },
      stepsImageDesktop: {
        type: String,
        attribute: 'steps-image-desktop',
      },
      additionalEntryLink: {
        type: String,
        attribute: 'additional-entry-link',
      },
      shopNowLink: {
        type: String,
        attribute: 'shop-now-link',
      },
      shopSuccessLink: {
        type: String,
        attribute: 'shop-success-link',
      },
      shopSuccessLabel: {
        type: String,
        attribute: 'shop-success-label',
      },
      shopSuccessAriaLabel: {
        type: String,
        attribute: 'shop-success-aria-label',
      },
      colour: {
        type: String,
        attribute: 'colour',
      },
      amount: {
        type: Number,
        attribute: 'amount',
      },
      privacyDisclaimerHtml: {
        type: String,
        attribute: 'privacy-disclaimer-html',
      },
      simple: {
        type: Boolean,
        attribute: 'simple',
      },
    };
  }

  constructor() {
    super();
    if (this.hasAttribute('simple')) {
      this.useUrlParams = false;
    }
    this.multiStep = [
      ...super.multiStep,
      { name: 'extra-chance', render: this.renderExtraChance.bind(this) },
    ];
  }

  get centAmount() {
    return Number(this.amount) * 100;
  }

  connectedCallback() {
    super.connectedCallback();

    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.get(this.id) && searchParams.get('override') === 'true')
      return;

    this.handleFlow();
  }

  async handleSubmit(event) {
    event.preventDefault();

    const fields = this.objectData;
    fields['metafield.competitions.first_entered.date_time'] = new Date()
      .toISOString()
      .split('Z')[0];

    const body = {
      fields,
      config: this.region,
      giveawayHandle: this.giveawayHandle,
    };

    try {
      const response = await fetch(this.action, {
        method: this.method,
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-wf-token': this.token,
          'x-wf-value': this.value,
        },
      });

      const data = await response.json();

      if (!data.success) {
        this.handleErrors(
          data.errors || data.error || 'An unknown error occurred'
        );
        return;
      }

      const snakeCaseHandle = this.giveawayHandle.replaceAll('-', '_');

      if (data.existing == false) {
        window.dispatchEvent(
          new CustomEvent('analytics:gtag', {
            detail: {
              event: `new_customer_${snakeCaseHandle}`,
              params: { region: this.region },
            },
          })
        );
      }

      window.dispatchEvent(
        new CustomEvent('analytics:gtag', {
          detail: {
            event: `main_entry_${snakeCaseHandle}`,
            params: { region: this.region },
          },
        })
      );

      // Pixels: Meta Lead and TikTok SubmitForm
      try {
        window?.fbq('track', 'Lead', {
          region: this.region,
        });
      } catch (err) {
        console.warn('Meta Lead tracking error', err);
      }

      try {
        window?.ttq?.track('SubmitForm', {
          region: this.region,
        });
      } catch (err) {
        console.warn('TikTok SubmitForm tracking error', err);
      }

      this.handleSuccess();
    } catch (error) {
      this.handleErrors(error);
    }
  }

  handleEnterGiveaway(order) {
    this.currentOrder = order;

    this.emitEvent('order-selected', { detail: order });
  }

  getGiveawayShopHref() {
    return (
      this.shopSuccessLink || this.shopNowLink || window.Shopify.routes.root
    );
  }

  getGiveawayShopButtonLabel() {
    const trimmed = this.shopSuccessLabel?.trim();
    if (trimmed) {
      return trimmed;
    }
    return window.theme.strings.giveaway.success_no_extra_chance_button;
  }

  getGiveawayShopButtonAriaLabel() {
    const trimmed = this.shopSuccessAriaLabel?.trim();
    if (trimmed) {
      return trimmed;
    }
    return this.getGiveawayShopButtonLabel();
  }

  resolveSuccessEntriesCount() {
    const orders = Array.isArray(this.customerDetails?.orders)
      ? this.customerDetails.orders
      : [];
    return orders.filter((order) => order.order_entered).length;
  }

  handleFlow() {
    if (!this.customerDetails?.logged_in || this.simple) {
      this.updateFormState('initial');
      return;
    }

    const hasEnteredGiveaway = this.customerDetails?.has_entered_giveaway;

    if (!hasEnteredGiveaway) {
      this.updateFormState('initial');
      return;
    }

    this.updateFormState('extra-chance');
    return;
  }

  renderInitial() {
    return html` <div class="giveaway_form_initial">
      ${this.simple
        ? nothing
        : html`<h1 class="giveaway_form_initial_title">
            ${window.theme.strings.giveaway.title}
          </h1>`}
      ${this.simple ? nothing : this.renderStepsImage()}
      <div class="giveaway_form_initial_form_title_wrapper">
        <h2 class="giveaway_form_initial_form_title">
          ${window.theme.strings.giveaway.enter}
        </h2>
        <span class="giveaway_form_initial_form_secondary">
          ${window.theme.strings.giveaway.enter_secondary}
        </span>
      </div>
      ${!this.customerDetails && !this.simple
        ? html`<p class="giveaway_form_initial_login_link">
            ${unsafeHTML(window.theme.strings.giveaway.login_html)}
          </p>`
        : nothing}
      <form
        ${ref((el) => (this.form = el))}
        method=${ifDefined(this.method)}
        ?novalidate=${this.novalidate}
        enctype=${ifDefined(this.enctype)}
      >
        ${this.renderFormElements()} ${this.renderButton()}
        ${this.renderErrors()} ${this.renderPrivacyDisclaimer()}
        ${this.simple
          ? nothing
          : html`<p class="giveaway_form_initial_additional_entry_link">
              ${unsafeHTML(
                window.theme.strings.giveaway.additional_entry_html.replace(
                  '{{ link }}',
                  this.additionalEntryLink
                )
              )}
            </p>`}
      </form>
    </div>`;
  }

  renderLogin() {
    return html`<div class="giveaway_form_login">
      <div class="giveaway_form_login_title_container">
        <h1 class="giveaway_form_login_title">
          ${window.theme.strings.giveaway.extra_chance_login_title}
        </h1>
        <h2 class="giveaway_form_login_subtitle">
          ${window.theme.strings.giveaway.extra_chance_login_subtitle}
        </h2>
      </div>
      <a
        href="${window.theme.loginLink}"
        class="btn btn-primary"
        aria-label="${window.theme.strings.giveaway.extra_chance_login_button}"
      >
        ${window.theme.strings.giveaway.extra_chance_login_button}
      </a>
    </div>`;
  }

  // LOGGED & ENTERED
  renderExtraChance() {
    if (!this.customerDetails?.logged_in) {
      return this.renderLogin();
    }

    const enteredGiveaway = this.customerDetails?.has_entered_giveaway;

    if (!enteredGiveaway) {
      return this.renderInitial();
    }

    const hasOrders = this.customerDetails?.orders?.length > 0;

    const ordersWithinGiveawayPeriod =
      this.customerDetails?.orders?.filter(
        (order) => order.order_within_giveaway_period
      ) ?? [];

    const allOrdersEntered = ordersWithinGiveawayPeriod.every(
      (order) => order.giveaway_entered
    );

    const ordersOver50 =
      this.customerDetails?.orders?.filter(
        (order) =>
          Number(order.order_total) >= this.centAmount &&
          order.order_within_giveaway_period
      ) ?? [];

    if (
      !hasOrders ||
      ordersWithinGiveawayPeriod.length === 0 ||
      ordersOver50.length === 0
    ) {
      return html`${this.renderNoExtraChance()}`;
    }

    if (allOrdersEntered) {
      return html`${this.renderAllOrdersEntered()}`;
    }

    return html`${this.renderExtraChanceForm()}`;
  }

  renderAllOrdersEntered() {
    const allEnteredOrders = this.customerDetails?.orders?.filter(
      (order) => order.order_entered
    );

    const enteredEntriesAmount = allEnteredOrders.length;
    const content =
      window.theme.strings.giveaway.all_orders_entered_html.replace(
        '{{ entered_entries_amount }}',
        enteredEntriesAmount + 1
      );

    return html`<div class="giveaway_form_all_orders_entered">
      ${this.renderFieldset(allEnteredOrders)}
      <h1 class="giveaway_form_all_orders_entered_title">
        ${window.theme.strings.giveaway.extra_chance_success_title}
      </h1>
      <div class="giveaway_form_all_orders_entered_content">
        ${unsafeHTML(content)}
      </div>

      <a
        href="${this.getGiveawayShopHref()}"
        class="btn btn-primary"
        aria-label="${this.getGiveawayShopButtonAriaLabel()}"
      >
        ${this.getGiveawayShopButtonLabel()}
      </a>
    </div>`;
  }

  renderNoExtraChance() {
    return html`<div class="giveaway_form_no_extra_chance">
      <h1 class="giveaway_form_no_extra_chance_title">
        ${window.theme.strings.giveaway.no_extra_chance_title}
      </h1>
      <div class="giveaway_form_no_extra_chance_content">
        ${unsafeHTML(window.theme.strings.giveaway.no_extra_chance_html)}
      </div>

      <a
        href="${this.getGiveawayShopHref()}"
        class="btn btn-primary"
        aria-label="${this.getGiveawayShopButtonAriaLabel()}"
      >
        ${this.getGiveawayShopButtonLabel()}
      </a>
    </div>`;
  }

  renderFieldset(orders) {
    return html`
      <scroll-fade colour=${this.colour}>
        <fieldset
          class="giveaway_form_extra_chance_select_options"
          scroll-container
        >
          <legend class="visually-hidden">
            ${window.theme.strings.giveaway.extra_select}
          </legend>
          ${map(orders, (order) => {
            return html`
              <div class="giveaway_form_extra_chance_select_item">
                <button
                  type="button"
                  class="giveaway_form_extra_chance_select_button ${order.order_entered
                    ? '_entered'
                    : ''} ${order.order_total < this.centAmount
                    ? '_ineligible'
                    : ''} ${order.order_number ===
                  this.currentOrder?.order_number
                    ? '_selected'
                    : ''}"
                  @click=${() => this.handleEnterGiveaway(order)}
                  ?disabled=${order.order_entered ||
                  Number(order.order_total) < this.centAmount ||
                  !order.order_within_giveaway_period}
                >
                  ${order.order_entered
                    ? html`<svg
                        width="22"
                        height="21"
                        viewBox="0 0 22 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="giveaway_form_extra_chance_select_check"
                      >
                        <path
                          d="M21 10.5C21 15.7034 16.5672 20 11 20C5.4328 20 1 15.7034 1 10.5C1 5.29663 5.4328 1 11 1C16.5672 1 21 5.29663 21 10.5Z"
                          fill="white"
                          stroke="#72C452"
                          stroke-width="2"
                        />
                        <path
                          d="M6.38672 11.25L9.22543 14.25L16.3222 6.75"
                          stroke="black"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg> `
                    : nothing}
                  <span class="giveaway_form_extra_chance_select_number"
                    >${order.order_number}</span
                  >
                  <span class="giveaway_form_extra_chance_select_price"
                    >${formatMoney(order.order_total)}</span
                  >
                </button>
              </div>
            `;
          })}
        </fieldset>
      </scroll-fade>
    `;
  }

  renderExtraChanceForm() {
    const ordersOver50 =
      this.customerDetails?.orders?.filter(
        (order) =>
          Number(order.order_total) >= this.centAmount &&
          order.order_within_giveaway_period
      ) ?? [];

    const allEnteredOrders = ordersOver50.every((order) => order.order_entered);

    if (!this.currentOrder) {
      const firstEligibleOrder = ordersOver50.find(
        (order) => !order.order_entered
      );

      if (!firstEligibleOrder && allEnteredOrders) {
        return html`${this.renderAllOrdersEntered()}`;
      }

      if (!firstEligibleOrder) {
        return html`${this.renderNoExtraChance()}`;
      }

      this.handleEnterGiveaway(firstEligibleOrder);
    }

    return html`<div class="giveaway_form_extra_chance">
        <h1 class="giveaway_form_extra_chance_title">
          ${window.theme.strings.giveaway.extra_chance_title}
        </h1>
        <h2 class="giveaway_form_extra_chance_form_title">
          ${window.theme.strings.giveaway.extra_chance_reenter}
        </h2>
        <div class="giveaway_form_extra_chance_select_container">
          <p class="giveaway_form_extra_chance_select">
            ${window.theme.strings.giveaway.extra_select}
          </p>
          ${this.renderFieldset(ordersOver50)}
        </div>
      </div>
      ${this.currentOrder
        ? html`<giveaway-additional-form
            current-order=${JSON.stringify(this.currentOrder)}
            action="https://australia-southeast1-foxtware.cloudfunctions.net/apexGiveawayAdditionalFormProcess"
            method="POST"
            enctype="application/json"
            token="${this.token}"
            value="${this.value}"
            region="${this.region}"
            giveaway-handle="${this.giveawayHandle}"
            customer-id="${this.customerDetails.id}"
            entries-count="${this.resolveSuccessEntriesCount() + 1}"
            shop-href="${this.getGiveawayShopHref()}"
            shop-label="${this.getGiveawayShopButtonLabel()}"
            shop-aria-label="${this.getGiveawayShopButtonAriaLabel()}"
          ></giveaway-additional-form>`
        : nothing} `;
  }

  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.updateFormState('success', true, true);
  }

  renderStepsImage() {
    return html`
    </picture>
      ${
        this.stepsImageMobile || this.stepsImageDesktop
          ? html`<picture>
              ${this.stepsImageMobile
                ? html` <source
                      media="(max-width: 767px)"
                      type="image/webp"
                      srcset="
                        ${`${this.stepsImageMobile}?width=375&format=webp 375w,
                      ${this.stepsImageMobile}?width=640&format=webp 640w,
                      ${this.stepsImageMobile}?width=768&format=webp 768w,
                      ${this.stepsImageMobile}?width=1536&format=webp 2x`}
                      "
                      sizes="(max-width: 375px) 375px,
                           (max-width: 640px) 640px,
                           (max-width: 768px) 768px,
                           100vw"
                    />
                    <source
                      media="(max-width: 767px)"
                      srcset="
                        ${`${this.stepsImageMobile}?width=375 375w,
                      ${this.stepsImageMobile}?width=640 640w,
                      ${this.stepsImageMobile}?width=768 768w,
                      ${this.stepsImageMobile}?width=1536 2x`}
                      "
                      sizes="(max-width: 375px) 375px,
                           (max-width: 640px) 640px,
                           (max-width: 768px) 768px,
                           100vw"
                    />`
                : nothing}
              ${this.stepsImageDesktop
                ? html` <source
                      media="(min-width: 768px)"
                      type="image/webp"
                      srcset="
                        ${`${this.stepsImageDesktop}?width=768&format=webp 768w,
                      ${this.stepsImageDesktop}?width=1024&format=webp 1024w,
                      ${this.stepsImageDesktop}?width=1280&format=webp 1280w,
                      ${this.stepsImageDesktop}?width=1536&format=webp 1536w,
                      ${this.stepsImageDesktop}?width=2048&format=webp 2x`}
                      "
                      sizes="(max-width: 768px) 768px,
                           (max-width: 1024px) 1024px,
                           (max-width: 1280px) 1280px,
                           (max-width: 1536px) 1536px,
                           100vw"
                    />
                    <source
                      media="(min-width: 768px)"
                      srcset="
                        ${`${this.stepsImageDesktop}?width=768 768w,
                      ${this.stepsImageDesktop}?width=1024 1024w,
                      ${this.stepsImageDesktop}?width=1280 1280w,
                      ${this.stepsImageDesktop}?width=1536 1536w,
                      ${this.stepsImageDesktop}?width=2048 2x`}
                      "
                      sizes="(max-width: 768px) 768px,
                           (max-width: 1024px) 1024px,
                           (max-width: 1280px) 1280px,
                           (max-width: 1536px) 1536px,
                           100vw"
                    />`
                : nothing}

              <img
                src="${this.stepsImageDesktop ||
                this.stepsImageMobile}?width=768"
                loading="eager"
                decoding="async"
                width="100%"
                height="auto"
                class="giveaway_form_extra_chance_steps_image"
                fetchpriority="high"
                alt="Step by step guide for entering the giveaway"
              />
            </picture>`
          : nothing
      }
    `;
  }

  renderFormElements() {
    const countriesList = Array.isArray(this.countries) ? this.countries : [];
    return html`
      <div class="form_split_elements">
        <float-field
          type="text"
          name="firstName"
          id="firstName"
          label="First Name"
          required
          value=${this.customerDetails?.customer?.first_name || ''}
          error="Please enter your first name"
          autocomplete="given-name"
          placeholder="First Name"
        ></float-field>
        <float-field
          type="text"
          name="lastName"
          id="lastName"
          label="Last Name"
          required
          value=${this.customerDetails?.customer?.last_name || ''}
          error="Please enter your last name"
          autocomplete="family-name"
          placeholder="Last Name"
        ></float-field>
      </div>
      <div class="form_split_elements">
        <float-field
          type="email"
          name="email"
          id="email"
          label="Email"
          required
          value=${this.customerDetails?.customer?.email || ''}
          error="Please enter your email"
          autocomplete="email"
          placeholder="Email"
        ></float-field>
        <float-select
          name="metafield.facts.gender.single_line_text_field"
          id="gender"
          label="Gender"
          required
          error="Please enter your gender"
          .options=${[
            {
              label: 'Gender',
              value: '',
              disabled: true,
              hidden: true,
              selected: this.customerDetails?.customer?.gender ? false : true,
            },
            {
              label: 'Female',
              value: 'Female',
              selected: this.customerDetails?.customer?.gender === 'Female',
            },
            {
              label: 'Male',
              value: 'Male',
              selected: this.customerDetails?.customer?.gender === 'Male',
            },
            {
              label: 'Other',
              value: 'Other',
              selected: this.customerDetails?.customer?.gender === 'Other',
            },
            {
              label: 'Prefer not to say',
              value: 'Prefer not to say',
              selected:
                this.customerDetails?.customer?.gender === 'Prefer not to say',
            },
          ]}
        ></float-select>
      </div>
      <div class="form_split_elements">
        <float-phone-field
          name="phone"
          id="phone"
          label="Phone"
          required
          error="Please enter your phone number"
          autocomplete="tel"
          placeholder="Phone"
          .isoCodes=${countriesList.map((country) => country.iso)}
          value=${this.customerDetails?.customer?.phone}
        ></float-phone-field>
        <float-date-field
          name="metafield.facts.birth_date.date"
          id="birthday"
          label="Birthday"
          required
          min="1900-01-01"
          max=${new Date().toISOString().split('T')[0]}
          error="Please enter your birthday"
          value=${this.customerDetails?.customer?.birthday || ''}
        ></float-date-field>
      </div>
      <float-country
        name="metafield.facts.country_code.single_line_text_field"
        id="country"
        label="Country/region of residence"
        error="Please enter your country/region of residence"
        required
        .options=${[
          {
            label: 'Country/region of residence',
            value: '',
            disabled: true,
            hidden: true,
            selected: this.customerDetails?.customer?.country ? false : true,
          },
          ...countriesList.map((country) => ({
            label: country.label,
            value: country.value,
            selected: this.customerDetails?.customer?.country === country.value,
          })),
        ]}
      ></float-country>
      <div class="form_split_elements">
        <float-province
          name="metafield.facts.province_code.single_line_text_field"
          id="province"
          label="Province"
          required
          error="Please enter your province"
          customer-province=${this.customerDetails?.customer?.province ||
          this.customerDetails?.customer?.state}
        ></float-province>
        <float-field
          name="metafield.facts.zip_code.single_line_text_field"
          id="postcode"
          label="Post Code / Zip code"
          required
          error="Please enter your post code / zip code"
          autocomplete="postal-code"
          placeholder="Post Code / Zip code"
          value=${this.customerDetails?.customer?.zip}
        ></float-field>
      </div>
      <div class="giveaway_form_reason_label_container">
        <p class="giveaway_form_reason_label">
          ${window.theme.strings.giveaway.reason_label}
        </p>
        <float-textarea
          name="metafield.competitions.entries_string.single_line_text_field"
          id="submission"
          required
          label="${window.theme.strings.giveaway.reason_label}"
          error="Please enter your reason"
          placeholder="Reason"
          max-words="25"
          maxLength="500"
          single-line
          no-inner-label
        ></float-textarea>
      </div>

      <float-checkbox
        name="terms"
        type="checkbox"
        id="terms"
        additional-info="${window.theme.strings.giveaway.terms_html}"
        required
        error="Please accept the terms and conditions"
      ></float-checkbox>
    `;
  }

  renderSimpleSuccess() {
    const strings = window.theme.strings.giveaway;
    const ordersOver50 =
      this.customerDetails?.orders?.filter(
        (order) =>
          Number(order.order_total) >= this.centAmount &&
          order.order_within_giveaway_period
      ) ?? [];

    const hasExtraChance = ordersOver50.length > 0;
    return html`
      <div class="giveaway_form_success giveaway_form_success--simple">
        <div class="giveaway_form_success_title_container">
          <h1 class="giveaway_form_success_title">
            ${strings.extra_chance_success_title}
          </h1>
          <h2 class="giveaway_form_success_subtitle">
            ${strings.extra_chance_success_subtitle}
          </h2>
          <div class="giveaway_form_success_content">
            ${hasExtraChance
              ? unsafeHTML(strings.success_has_extra_chance_html)
              : unsafeHTML(strings.success_no_extra_chance_html)}
          </div>
        </div>
        <div class="giveaway_form_success_actions">
          <button
            type="button"
            class="btn btn-primary"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent('modal-giveaway-form:close', {
                  bubbles: true,
                  composed: true,
                })
              );
            }}
          >
            ${unsafeHTML(strings.simple_success_button)}
          </button>
        </div>
      </div>
    `;
  }

  renderSuccess() {
    if (this.simple) {
      return this.renderSimpleSuccess();
    }

    const ordersOver50 =
      this.customerDetails?.orders?.filter(
        (order) =>
          Number(order.order_total) >= this.centAmount &&
          order.order_within_giveaway_period
      ) ?? [];

    const hasExtraChance = ordersOver50.some((order) => !order.order_entered);
    const strings = window.theme.strings.giveaway;
    const shopHref = this.getGiveawayShopHref();
    const shopLabel = this.getGiveawayShopButtonLabel();
    const shopAria = this.getGiveawayShopButtonAriaLabel();
    const reEnterLabel =
      (strings.success_re_enter_button &&
        strings.success_re_enter_button.trim()) ||
      strings.success_has_extra_chance_button;
    const reEnterAria =
      (strings.success_re_enter_aria_label &&
        strings.success_re_enter_aria_label.trim()) ||
      reEnterLabel;

    return html`
      <confetti-element></confetti-element>
      <div class="giveaway_form_success">
        <div class="giveaway_form_success_title_container">
          <h1 class="giveaway_form_success_title">${strings.success_title}</h1>
          <h2 class="giveaway_form_success_subtitle">
            ${strings.success_subtitle}
          </h2>
        </div>
        <div class="giveaway_form_success_content">
          ${hasExtraChance
            ? unsafeHTML(strings.success_has_extra_chance_html)
            : unsafeHTML(strings.success_no_extra_chance_html)}
          ${hasExtraChance
            ? unsafeHTML(strings.success_re_enter_cta_html)
            : nothing}
        </div>

        <div class="giveaway_form_success_actions">
          <a
            href="${shopHref}"
            class="btn btn-primary giveaway_form_success_btn_shop"
            aria-label="${shopAria}"
          >
            ${shopLabel}
          </a>
          ${hasExtraChance
            ? html`<button
                type="button"
                class="btn btn-primary giveaway_form_success_btn_pink"
                aria-label="${reEnterAria}"
                @click=${() => {
                  this.currentOrder = undefined;
                  this.updateFormState('extra-chance');
                }}
              >
                ${reEnterLabel}
              </button>`
            : nothing}
        </div>
      </div>
      ${!this.customerDetails?.logged_in
        ? html`<div class="giveaway_form_success_login">
            <h3 class="giveaway_form_success_login_title">
              ${window.theme.strings.giveaway.success_login_title}
            </h3>
            <div class="giveaway_form_success_login_content">
              ${unsafeHTML(window.theme.strings.giveaway.success_login_html)}
            </div>
            <a
              href="${window.theme.loginLink}"
              class="btn btn-primary"
              aria-label="${window.theme.strings.giveaway.success_login_button}"
            >
              ${window.theme.strings.giveaway.success_login_button}
            </a>
          </div> `
        : nothing}
    `;
  }

  renderButton() {
    return html`
      <button
        type="submit"
        class="btn btn-primary giveaway_form_button"
        aria-label="Submit giveaway form"
        ?disabled=${this.loading}
        aria-busy=${this.loading ? 'true' : 'false'}
      >
        ${this.loading
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : 'Submit'}
      </button>
    `;
  }

  renderPrivacyDisclaimer() {
    const themeFallback =
      window.theme?.strings?.giveaway?.privacy_disclaimer_html;
    const fromAttr = (
      this.getAttribute('privacy-disclaimer-html') || ''
    ).trim();
    const fromProp =
      (this.privacyDisclaimerHtml &&
        String(this.privacyDisclaimerHtml).trim()) ||
      '';
    const fromTheme = (themeFallback && String(themeFallback).trim()) || '';
    const htmlString = fromProp || fromAttr || fromTheme;
    if (!htmlString) return nothing;

    return html`
      <div class="giveaway_form_privacy_disclaimer newsletter_disclaimer">
        <div>${unsafeHTML(htmlString)}</div>
      </div>
    `;
  }
}

window.customElements.get('giveaway-form') ||
  window.customElements.define('giveaway-form', GiveawayForm);
